/**
 * DashboardTour — first-run coach-mark tour for the member dashboard.
 *
 * How it works:
 *   1. On mount, checks localStorage.dashboard_tour_seen_v1.  If already seen,
 *      renders nothing.
 *   2. Otherwise, waits ~800ms so the dashboard content settles, then reads
 *      the bounding-client-rect of each step's `target` selector, positions
 *      a highlight ring around it, and shows a popover card with copy.
 *   3. On Finish/Skip, sets the localStorage flag so future visits stay quiet.
 *
 * We intentionally do NOT use a heavy tour library (driver.js etc.) — a small
 * custom overlay keeps the bundle lean and lets us match the HomeLyph Pro
 * aesthetic exactly.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'dashboard_tour_seen_v1';

// Ordered list of steps.  Each `target` is a CSS selector.  If the element
// isn't found we skip that step gracefully.
const STEPS = [
  {
    target: '[data-testid="shell-brand"], [data-testid="brand-mark"]',
    title: 'Your workbench',
    body: 'This is Freeze Dry Academy. Click the logo any time to return here from anywhere in the app.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="tool-selector"]',
    title: 'Quick tools',
    body: 'Every calculator, video library and community tab is one click away from this pinned bar. It stays with you on every page.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="toolbar-cmdk-trigger"]',
    title: 'Jump anywhere with ⌘K',
    body: 'Press ⌘K (or Ctrl+K on Windows) from anywhere to launch this palette and jump to any tool, course or admin page.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="notification-bell"]',
    title: 'Notifications',
    body: 'Forum replies, new lessons in courses you follow, and academy-wide announcements land here. The red dot tells you how many are unread.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="module-card-0"]',
    title: 'Pick a tool',
    body: 'These are your workstations — Learning, AI Assistant, Business Tools and more. Hover the ? icon on any card for a quick description.',
    placement: 'top',
  },
  {
    target: '[data-testid="my-portal-btn"]',
    title: 'Your portal',
    body: 'Profile, equipment, saved batches and billing live in My Portal. Come back to fill in details whenever you have a minute.',
    placement: 'bottom',
  },
];

const highlightPad = 8;
const cardWidth = 340;
const cardMargin = 14;

const DashboardTour = ({ forceOpen = false, onFinish }) => {
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null); // {top,left,width,height}
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);

  // Decide whether to run
  useEffect(() => {
    if (forceOpen) { setVisible(true); return; }
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch (_) { /* localStorage unavailable */ }
  }, [forceOpen]);

  const step = STEPS[idx];

  // Compute the target rect + card position each time the step changes or
  // the window resizes.  If the target doesn't exist yet, retry a few times.
  useLayoutEffect(() => {
    if (!visible || !step) return undefined;
    let cancelled = false;
    let tries = 0;

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (!el) {
        if (tries++ < 10) {
          setTimeout(measure, 200);
        } else {
          // give up, advance
          setIdx((i) => Math.min(STEPS.length - 1, i + 1));
        }
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const r = el.getBoundingClientRect();
      const targetRect = {
        top: r.top - highlightPad,
        left: r.left - highlightPad,
        width: r.width + highlightPad * 2,
        height: r.height + highlightPad * 2,
      };
      setRect(targetRect);

      // Compute card position
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let top = targetRect.top + targetRect.height + cardMargin;
      let left = targetRect.left;
      const approxHeight = 200;
      if (step.placement === 'top' || top + approxHeight > vh - 20) {
        top = targetRect.top - approxHeight - cardMargin;
      }
      if (top < 20) top = 20;
      if (left + cardWidth > vw - 20) left = vw - cardWidth - 20;
      if (left < 20) left = 20;
      setCardPos({ top, left });
    };

    // Small delay so any transition finishes before measuring
    const t = setTimeout(measure, 60);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, { passive: true });
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
    };
  }, [visible, idx, step]);

  const close = (persist = true) => {
    setVisible(false);
    if (persist) {
      try { window.localStorage.setItem(STORAGE_KEY, '1'); } catch (_) { /* ignore */ }
    }
    onFinish && onFinish();
  };

  if (!visible || !step) return null;

  const isFirst = idx === 0;
  const isLast = idx === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] pointer-events-none"
      role="dialog"
      aria-label="Dashboard tour"
      data-testid="dashboard-tour"
    >
      {/* Backdrop with a punched-out highlight */}
      <div className="absolute inset-0 bg-navy/70 pointer-events-auto" onClick={() => close(true)} />
      {rect && (
        <div
          className="absolute rounded-2xl border-2 border-primary shadow-[0_0_0_9999px_rgba(10,31,51,0.72)] transition-all duration-300 pointer-events-none"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
          data-testid="dashboard-tour-highlight"
        />
      )}

      {/* Card */}
      <div
        ref={cardRef}
        className="absolute pointer-events-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        style={{ top: cardPos.top, left: cardPos.left, width: cardWidth }}
        data-testid="dashboard-tour-card"
      >
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-primary/15 text-primary flex items-center justify-center">
              <Sparkles className="w-3 h-3" />
            </div>
            <span className="eyebrow">Step {idx + 1} of {STEPS.length}</span>
          </div>
          <button
            type="button"
            onClick={() => close(true)}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md"
            aria-label="Close tour"
            data-testid="dashboard-tour-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 pb-3">
          <div className="font-heading text-base font-extrabold tracking-tight text-foreground">
            {step.title}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mt-1">
            {step.body}
          </p>
        </div>
        {/* Step dots */}
        <div className="px-4 flex items-center gap-1.5">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
            />
          ))}
        </div>
        {/* Actions */}
        <div className="px-4 py-3 flex items-center justify-between border-t border-border mt-3 bg-muted/20">
          <button
            type="button"
            onClick={() => close(true)}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
            data-testid="dashboard-tour-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition-colors"
                data-testid="dashboard-tour-back"
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            )}
            {isLast ? (
              <button
                type="button"
                onClick={() => close(true)}
                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-extrabold hover:opacity-90 transition-opacity"
                data-testid="dashboard-tour-finish"
              >
                Got it <Sparkles className="w-3 h-3" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIdx((i) => Math.min(STEPS.length - 1, i + 1))}
                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-extrabold hover:opacity-90 transition-opacity"
                data-testid="dashboard-tour-next"
              >
                Next <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTour;
